/**
 * 영속성 파사드 — 프로젝트·워크플로·캔버스 저장을 어디로 보낼지 고른다. projectSlice 등은 이 파일만 import 한다.
 *
 * 기본은 **저장 API(storeRepo → BE1 /api/store)** 다. 웹판·스탠드얼론 모두 BE1 이 저장소를 고르고 프로젝트 권한을
 * 판정한다(docs/PROJECT_ACCESS.md, B안). 웹판에서 BE1 이 아직 준비되지 않았으면(QBase 서비스 계정 미설정 —
 * /api/store/info 의 ready=false) 예전처럼 브라우저가 QBase 를 직접 부른다(openflowRepo). 준비 여부는 처음 한 번 묻고
 * 기억한다 — 서버를 준비시킨 뒤에는 새로고침하면 새 경로로 바뀐다.
 */
import { IS_LOCAL } from '../config/mode';
import * as storeImpl from './storeRepo';
import * as qbaseImpl from './openflowRepo';
import type { CanvasDoc } from './openflowRepo';
import type { Project, Workflow } from '../store/project/projectSlice';
import { dehydrateNodes, hydrateNodes } from '../components/workflow/utils/definitionRefs';

type Impl = typeof storeImpl | typeof qbaseImpl;

let chosen: Promise<Impl> | null = null;

/** 어느 구현을 쓸지 — 스탠드얼론은 늘 저장 API, 웹판은 서버가 준비됐을 때만 */
function pick(): Promise<Impl> {
  if (IS_LOCAL) return Promise.resolve(storeImpl);
  if (!chosen) {
    chosen = storeImpl.storeReady().then((ready) => (ready ? storeImpl : qbaseImpl));
    // 서버에 묻지 못했으면 다음에 다시 묻는다 (로그인 전에 불렸을 수 있다)
    chosen.then((impl) => { if (impl === qbaseImpl) chosen = null; }).catch(() => { chosen = null; });
  }
  return chosen;
}

/** 지금 저장 API(권한 판정 있음)를 쓰고 있나 — 구성원 화면·역할 표시는 이때만 뜬다 */
export async function usesStoreApi(): Promise<boolean> {
  return (await pick()) === storeImpl;
}

export const listProjects = async (): Promise<Project[]> => (await pick()).listProjects();
export const listWorkflows = async (projectId: string): Promise<Workflow[]> => (await pick()).listWorkflows(projectId);
/** 데이터 노드는 정의 id 만 저장된다 — 읽을 때 정의의 값을 채운다 (components/workflow/utils/definitionRefs) */
export const loadCanvas = async (workflowId: string, verNo: number): Promise<CanvasDoc | null> => {
  const doc = await (await pick()).loadCanvas(workflowId, verNo);
  return doc ? { ...doc, nodes: hydrateNodes(doc.nodes) } : doc;
};
export const listCanvasVersions = async (workflowId: string) => (await pick()).listCanvasVersions(workflowId);
export const createProject = async (input: { name: string; description?: string }, ownerUsrid: string): Promise<Project> =>
  (await pick()).createProject(input, ownerUsrid);
export const updateProject = async (p: Project): Promise<void> => (await pick()).updateProject(p);
export const createWorkflow = async (input: { projectId: string; name: string; description?: string; icon?: string; folder?: string }): Promise<Workflow> =>
  (await pick()).createWorkflow(input);
export const updateWorkflowMeta = async (w: { id: string; name: string; description?: string; folder?: string }): Promise<void> =>
  (await pick()).updateWorkflowMeta(w);
export const retireWorkflow = async (workflowId: string): Promise<void> => (await pick()).retireWorkflow(workflowId);
/** 쓸 때는 정의가 주인인 칸을 걷어 내고 정의 id 만 남긴다 */
export const saveCanvas = async (workflowId: string, curVerNo: number, doc: CanvasDoc, regUsrid: string): Promise<number> =>
  (await pick()).saveCanvas(workflowId, curVerNo, { ...doc, nodes: dehydrateNodes(doc.nodes) }, regUsrid);

/** 구성원 — 저장 API 에만 있다(예전 경로에서는 권한이 없어 빈 값) */
export const getProjectMembers = async (projectId: string) =>
  ((await pick()) === storeImpl ? storeImpl.getProjectMembers(projectId) : { owner: '', members: [], myRole: null });
export const setProjectMembers = storeImpl.setProjectMembers;
/** 샘플 프로젝트 최신으로 바꾸기 — 저장 API 에만 있다(도움말 메뉴는 usesStoreApi 일 때만 보인다) */
export const refreshSample = storeImpl.refreshSample;

export type { CanvasDoc, CanvasVersionInfo } from './openflowRepo';
export type { ProjectRole, ProjectMember } from './storeRepo';
